import { formatDateTime, leadStageLabel } from '@crm/shared';
import { Card, CardContent, cn, Spinner } from '@crm/ui';
import { CheckCheck, type LucideIcon, Send, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import { EmptyState } from '@/components';
import { LEADS_ROUTES } from '@/modules/leads/api';
import { useCampaignMetrics, useCampaignRuns } from '../api';

function Numero({
  label,
  value,
  icon: Icone,
  tone,
}: {
  label: string;
  value: number;
  icon: LucideIcon;
  tone: string;
}) {
  return (
    <div className="flex items-center gap-3 rounded-card border border-border bg-card px-4 py-3">
      <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', tone)}>
        <Icone className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-2xl font-semibold leading-tight">{value}</p>
        <p className="truncate text-sm text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

/** O que a campanha trouxe: entregas, funil e o historico de cada disparo. */
export function ResultsTab({ campaignId }: { campaignId: string }) {
  const metrics = useCampaignMetrics(campaignId);
  const runs = useCampaignRuns(campaignId);

  const maior = Math.max(1, ...(metrics.data?.funnel.map((linha) => linha.total) ?? [0]));
  const emAndamento = runs.data?.data.find((run) => !run.finishedAt);

  return (
    <div className="space-y-4">
      {/* Disparo acontece na fila e leva minutos: sem esta linha a tela fica
          igual antes e depois de apertar o botao. */}
      {emAndamento ? (
        <Card variant="flush">
          <CardContent className="space-y-2 py-4">
            <div className="flex items-center gap-2">
              <Spinner />
              <p className="font-medium">Enviando agora</p>
              <p className="ml-auto text-sm tabular-nums text-muted-foreground">
                {emAndamento.sent + emAndamento.failed} de {emAndamento.total}
              </p>
            </div>
            <div className="h-2 rounded-full bg-muted">
              <div
                className="h-2 rounded-full bg-primary transition-all"
                style={{
                  width: `${Math.round(((emAndamento.sent + emAndamento.failed) / Math.max(1, emAndamento.total)) * 100)}%`,
                }}
              />
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-3">
        <Numero
          label="Enviadas"
          value={metrics.data?.sent ?? 0}
          icon={Send}
          tone="bg-primary/10 text-primary"
        />
        <Numero
          label="Entregues"
          value={metrics.data?.delivered ?? 0}
          icon={CheckCheck}
          tone="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400"
        />
        <Numero
          label="Falharam"
          value={metrics.data?.failed ?? 0}
          icon={TriangleAlert}
          tone="bg-destructive/10 text-destructive"
        />
      </div>

      <Card variant="flush">
        <CardContent className="space-y-3 py-5">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-medium">Funil da campanha</h2>
            <Link
              to={`${LEADS_ROUTES.BOARD}?campaignId=${campaignId}`}
              className="text-sm text-primary hover:underline"
            >
              Ver no quadro
            </Link>
          </div>

          {metrics.isPending ? (
            <Spinner />
          ) : metrics.data?.funnel.length ? (
            <ul className="space-y-2">
              {metrics.data.funnel.map((linha) => (
                <li key={linha.stage} className="space-y-1">
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="truncate">{leadStageLabel(linha.stage)}</span>
                    <span className="font-medium">{linha.total}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted">
                    <div
                      className="h-2 rounded-full bg-primary"
                      style={{ width: `${(linha.total / maior) * 100}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-4 text-sm text-muted-foreground">
              Nenhum lead ainda - dispare a campanha para ver o funil.
            </p>
          )}
        </CardContent>
      </Card>

      <Card variant="flush">
        <CardContent className="p-0">
          <h2 className="border-b border-border py-3 font-medium">Disparos</h2>

          {runs.data?.data.length ? (
            <ul className="divide-y divide-border">
              {runs.data.data.map((run) => (
                <li key={run.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{formatDateTime(run.startedAt)}</p>
                    <p className="truncate text-sm text-muted-foreground">
                      {run.triggeredByName ?? 'Disparo agendado'}
                      {run.finishedAt ? '' : ' - em andamento'}
                    </p>
                  </div>
                  <p className="shrink-0 text-right text-sm">
                    <span className="font-medium">{run.sent}</span>
                    <span className="text-muted-foreground">/{run.total}</span>
                    {/* Quem ja tinha recebido campanha nas ultimas 24h nao
                        entra no disparo - sem esta linha o zero parecia erro.
                        So depois de terminar: no meio do envio, o que falta
                        ainda vai sair. */}
                    {run.finishedAt && run.total - run.sent - run.failed > 0 ? (
                      <span className="block text-xs text-muted-foreground">
                        {run.total - run.sent - run.failed} ja tinham recebido
                      </span>
                    ) : null}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState title="Nenhum disparo ainda" />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
